import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import {
  ProjectsOverviewControls,
  ProjectsOverviewControlsModel
} from "./ProjectsOverviewControls"
import {
  ProjectsOverviewPageProjectList,
  ProjectsOverviewPageProjectListModel
} from "./ProjectsOverviewPageProjectList"
import "./styles/_projects-overview.scss"

export interface ProjectsOverviewPageModel {
  isLoading?: boolean
  isProjectsServiceAvailable?: boolean

  projectListModel: ProjectsOverviewPageProjectListModel
  controlsModel: ProjectsOverviewControlsModel
  onNewProject: () => void
}

export interface ProjectsOverviewPageProps extends ProjectsOverviewPageModel {
  title: React.ReactNode
}

export const ProjectsOverviewPageView = React.memo(
  ({
    title,
    isLoading,
    isProjectsServiceAvailable = true,
    controlsModel,
    projectListModel,
    onNewProject
  }: ProjectsOverviewPageProps) => {
    const { t } = useTranslation()

    const newProjectButtonProps: Action = useMemo(
      () => ({
        "data-testid": "project__actions__new",
        icon: IconName.PLUS_CIRCLE,
        label: t("Project.Actions.Create"),
        disabled: isLoading || !isProjectsServiceAvailable,
        onClick: onNewProject
      }),
      [t, isLoading, isProjectsServiceAvailable, onNewProject]
    )

    return (
      <div
        id="projects-overview"
        className="projects-overview"
        data-testid="projects-overview-page"
      >
        <div className="projects-overview__header">
          <h1 className="projects-overview__title">{title}</h1>
          <Button
            variant={ButtonVariant.PRIMARY}
            {...newProjectButtonProps}
            className="projects-overview__new-project-button mobile-icon-only"
          />
        </div>

        {isProjectsServiceAvailable && (
          <div className="projects-overview__controls">
            <ProjectsOverviewControls {...controlsModel} />
          </div>
        )}

        <div className="projects-overview__grid">
          <ProjectsOverviewPageProjectList {...projectListModel} />
        </div>
      </div>
    )
  }
)
